<template>
	<div
		ref="listRef"
		class="relative flex w-full md:w-[calc(33%-28px)] min-h-[56px] mt-3 md:mt-2 pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 border-b-[1px] border-gray-light md:border-0"
	>
		<div @click="showList = !showList" class="w-full flex-col cursor-pointer">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("mainSearchbar.location.title") }}</span>
			<input
				v-if="canType || selectedLocations.length == 0"
				ref="inputRef"
				v-model="inputVmodel"
				class="w-full outline-none border-none bg-transparent"
				type="text"
				:placeholder="placeholder"
			/>
			<span
				v-else
				@click="canType = true"
				class="block overflow-hidden overflow-ellipsis whitespace-nowrap w-[190px] cursor-default"
			>
				{{
					selectedLocations
						.map((l) => l.name)
						.map((n) => getNameByLocale(n))
						.join(", ")
				}}
			</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-show="showList"
			class="absolute p-5 left-0 md:-left-8 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] sm:max-w-[400px] max-h-[350px] bg-white border border-dj-gray-light rounded-big flex flex-col z-10"
		>
			<client-only>
				<h3 class="font-medium mb-[10px]">{{ $t("mainSearchbar.location.results") }}</h3>
				<div class="flex flex-col gap-y-[10px] overflow-auto">
					<div v-if="false && (getCurrentRegionLocation.country.showCountry || inputVmodel.length < 1)">
						{{ getNameByLocale(getCurrentRegionLocation.country.name) }}
					</div>
					<template v-for="(location, index) in getCurrentRegionLocation.list" :key="`region-${index}`">
						<BaseCheckbox
							v-show="location.showLocation || inputVmodel.length < 1"
							v-model="location.value"
							v-if="getNameByLocale(location.name).length"
							@change="onChange($event, location)"
						>
							{{ getNameByLocale(location.name) }}
						</BaseCheckbox>
					</template>

					<template v-for="(country, index) in getOtherLocations" :key="`other-${index}`">
						<div v-if="false && (country.showCountry || inputVmodel.length < 1)">
							{{ getNameByLocale(country.name) }}
						</div>
						<template v-for="(location, ind) in country.children" :key="`other-${index}-${ind}`">
							<BaseCheckbox
								v-show="location.showLocation || inputVmodel.length < 1"
								v-model="location.value"
								@change="onChange($event, location)"
							>
								{{ getNameByLocale(location.name) }}
							</BaseCheckbox>
						</template>
					</template>
				</div>
			</client-only>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, computed, onMounted } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { type locationObject } from "@@/types";

type LocationName = Partial<Record<string, string>>;

type LocationChild = locationObject & {
	showLocation?: boolean;
};

type LocationCountry = {
	slug: string;
	name: LocationName;
	children: LocationChild[];
	showCountry?: boolean;
	value?: boolean;
};

export default defineComponent({
	name: "SearchbarMainLocation",
	props: {
		modelValue: {
			type: Array as PropType<locationObject[]>,
			required: true,
		},
		filterList: {
			type: Array as PropType<LocationCountry[]>,
			default: () => [],
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	setup(props, { emit }) {
		const { locale } = useI18n();
		const region = ref("");
		const inputRef = ref(null);
		const listRef = ref(null);
		const inputVmodel = ref("");
		const locationsData = ref<LocationCountry[]>([]);
		const locationsWithValue = ref<LocationCountry[]>([]);
		const selectedLocations = ref<locationObject[]>([]);
		const showList = ref(false);
		const canType = ref(true);

		const fallbackCountry: LocationCountry = {
			slug: "",
			name: { en: "", hu: "", ro: "" },
			children: [],
			showCountry: true,
			value: false,
		};

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code == "HU" ? "hungary" : "romania";
		});

		if (props.filterList.length > 0) {
			locationsData.value = props.filterList;
			setDataArrays();
		}

		function setDataArrays() {
			selectedLocations.value = [];
			locationsWithValue.value = transformArray();
		}

		function transformArray() {
			const sortedLocations = sortArrayByObjectByLocale(locationsData.value, "name", locale.value)
				.value as LocationCountry[];

			return sortedLocations.map((location: LocationCountry) => {
				return {
					...location,
					value: false,
					showCountry: true,
					children: (location.children || []).map((child: LocationChild) => ({
						...child,
						value: false,
						showLocation: true,
					})),
				};
			});
		}

		function getNameByLocale(name: LocationName) {
			const lang = locale.value as keyof LocationName;
			return name?.[lang] || name?.en || "";
		}

		const getCurrentRegionLocation = computed(() => {
			const country =
				locationsWithValue.value.filter((country) => country.slug == region.value)[0] || fallbackCountry;
			const list = sortArrayByObjectByLocale(country.children, "name", locale.value).value as LocationChild[];
			return { list: list, country: country };
		});

		const getOtherLocations = computed(() => {
			const otherLocations = locationsWithValue.value.filter((country) => country.slug != region.value);
			otherLocations.forEach((el, index) => {
				otherLocations[index].children = sortArrayByObjectByLocale(el.children, "name", locale.value)
					.value as LocationChild[];
			});

			return otherLocations;
		});

		watch(inputVmodel, () => {
			if (inputVmodel.value.length > 0) {
				const pattern = new RegExp(inputVmodel.value, "gi");
				locationsWithValue.value.forEach((country, index) => {
					let showThisCountry = false;
					country.children.forEach((location, ind) => {
						if (getNameByLocale(location.name).match(pattern)) {
							locationsWithValue.value[index].children[ind].showLocation = true;
							showThisCountry = true;
						} else {
							locationsWithValue.value[index].children[ind].showLocation = false;
						}
					});
					locationsWithValue.value[index].showCountry = showThisCountry;
				});

				showList.value = true;
			} else {
				locationsWithValue.value.forEach((country, index) => {
					country.children.forEach((location, ind) => {
						locationsWithValue.value[index].children[ind].showLocation = true;
					});
					locationsWithValue.value[index].showCountry = true;
				});
			}
		});

		const { focused: inputFocus } = useFocus(inputRef);

		onClickOutside(listRef, () => {
			showList.value = false;
			inputVmodel.value = "";
		});

		onClickOutside(inputRef, () => {
			canType.value = false;
		});

		function onChange(event: boolean, settlement: locationObject) {
			if (event) {
				if (!selectedLocations.value.some((location) => location.id === settlement.id)) {
					selectedLocations.value.push(settlement);
				}
			} else {
				selectedLocations.value = selectedLocations.value.filter((location) => location.id !== settlement.id);
			}
			emit("update:modelValue", selectedLocations.value);
		}

		return {
			inputRef,
			inputVmodel,
			inputFocus,
			listRef,
			showList,
			onChange,
			selectedLocations,
			canType,
			locale,
			region,
			getCurrentRegionLocation,
			getOtherLocations,
			getNameByLocale,
			sortArrayByObjectByLocale,
		};
	},
});
</script>
